import { RootState } from '@/store/store';
import { FiltersGroup } from '@/components/uiKit/FiltersGroup';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setFaultsCurrentFiltersValues,
  setFaultsCurrentFiltersData,
  setFaultsAllFiltersValues,
  setFaultsInitialFiltersValues,
  getFaultsFiltersData,
  getFaultsFiltersValues,
  getFilteredFaults,
} from '@/store/slices/assetsPreviewSlice';
import { FiltersValues } from '@/components/uiKit/FiltersGroup/types';
import { useTranslation } from 'react-i18next';
import { useFetchFaults } from '@/hooks';

export const FaultsFilter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { faultsRaw: faults, isLoading } = useFetchFaults();
  const {
    faultsCurrentFiltersData,
    faultsInitialFiltersValues,
    faultsCurrentFiltersValues,
    faultsAllFiltersValues,
  } = useSelector((state: RootState) => state.assetsPreview);

  // Track the data we last processed to avoid unnecessary recalculations
  const lastProcessedDataRef = useRef<unknown>(null);
  // Store the current filters at the time of processing to avoid stale closure issues
  const currentFiltersRef = useRef<FiltersValues | undefined>(undefined);

  // Keep the ref updated with latest filter values
  useEffect(() => {
    currentFiltersRef.current = faultsCurrentFiltersValues;
  }, [faultsCurrentFiltersValues]);

  // Update filters when faults data changes
  useEffect(() => {
    if (!faults || faults.length === 0) return;

    // Skip if we've already processed this exact data
    if (lastProcessedDataRef.current === faults) return;
    lastProcessedDataRef.current = faults;

    // Get fresh filter values based on current data
    const freshFiltersValues = getFaultsFiltersValues(faults);

    // Check if we have existing filter selections to preserve (using ref to get latest)
    const existingFilters = currentFiltersRef.current;
    const hasExistingFilters = existingFilters && Object.keys(existingFilters).length > 0;

    if (hasExistingFilters) {
      // Merge existing selections with new data availability
      // Keep user's selections but only for options that exist in allFiltersValues
      const mergedCurrentFiltersValues: FiltersValues = {};

      Object.keys(freshFiltersValues.allFiltersValues).forEach((filterLabel) => {
        const existingSelection = existingFilters[filterLabel];
        const availableOptions = freshFiltersValues.allFiltersValues[filterLabel];

        if (existingSelection && existingSelection.length > 0) {
          // Keep only selections that are valid options (exist in all values)
          // This preserves selections even if they result in 0 matches (user can reset)
          mergedCurrentFiltersValues[filterLabel] = existingSelection.filter((val) =>
            availableOptions.includes(val)
          );
          // If all selections were invalid, use the initial values for this data
          if (mergedCurrentFiltersValues[filterLabel].length === 0) {
            mergedCurrentFiltersValues[filterLabel] = freshFiltersValues.initialFiltersValues[filterLabel] || [];
          }
        } else {
          // No existing selection for this filter, use fresh initial values
          mergedCurrentFiltersValues[filterLabel] = freshFiltersValues.initialFiltersValues[filterLabel] || [];
        }
      });

      // Calculate filter data with merged selections
      const filtersData = getFaultsFiltersData(faults, mergedCurrentFiltersValues, freshFiltersValues.allFiltersValues);

      dispatch(setFaultsAllFiltersValues(freshFiltersValues.allFiltersValues));
      dispatch(setFaultsInitialFiltersValues(freshFiltersValues.initialFiltersValues));
      dispatch(setFaultsCurrentFiltersValues(mergedCurrentFiltersValues));
      dispatch(setFaultsCurrentFiltersData(filtersData));
    } else {
      // First time initialization - use fresh values
      const filtersData = getFaultsFiltersData(
        faults,
        freshFiltersValues.currentFiltersValues,
        freshFiltersValues.allFiltersValues
      );

      dispatch(setFaultsAllFiltersValues(freshFiltersValues.allFiltersValues));
      dispatch(setFaultsInitialFiltersValues(freshFiltersValues.initialFiltersValues));
      dispatch(setFaultsCurrentFiltersValues(freshFiltersValues.currentFiltersValues));
      dispatch(setFaultsCurrentFiltersData(filtersData));
    }
  }, [faults, dispatch]);

  const handleFiltersValuesChange = (filtersValues: FiltersValues) => {
    if (!faultsAllFiltersValues || !faults) return;
    const filtersData = getFaultsFiltersData(faults, filtersValues, faultsAllFiltersValues);
    dispatch(setFaultsCurrentFiltersValues(filtersValues));
    dispatch(setFaultsCurrentFiltersData(filtersData));
  };

  const filteredFaults =
    faults && faultsCurrentFiltersValues ? getFilteredFaults(faults, faultsCurrentFiltersValues) : null;

  const drawerCloseBtnText = filteredFaults
    ? t('assetsPreview.filter.showFaults', { count: filteredFaults.length, defaultValue: `Show ${filteredFaults.length} Faults` })
    : '';

  if (!faults || isLoading) return null;

  return (
    <>
      {faultsCurrentFiltersData && faultsAllFiltersValues && faultsInitialFiltersValues && faultsCurrentFiltersValues && (
        <FiltersGroup
          currentFiltersData={faultsCurrentFiltersData}
          handleFiltersValuesChange={handleFiltersValuesChange}
          initialFiltersValues={faultsInitialFiltersValues}
          currentFiltersValues={faultsCurrentFiltersValues}
          allFiltersValues={faultsAllFiltersValues}
          drawerCloseBtnText={drawerCloseBtnText}
          hideQuickFiltersResolution={1440}
        />
      )}
    </>
  );
};
